// /reports/payments — Payment Breakdown (PAGES.md §3.44)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { PieChart } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { PaymentModeRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = PaymentModeRow & { id: string };

const MODE_COLORS: Record<PaymentModeRow['mode'], string> = {
  CASH: '#10b981',
  UPI: '#6366f1',
  CARD: '#8b5cf6',
  WALLET: '#f59e0b',
  SPLIT: '#ec4899',
};

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    {
      key: 'mode',
      header: 'Payment Mode',
      render: (_v, row) => (
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: MODE_COLORS[row.mode] }} />
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.mode}</span>
        </div>
      ),
    },
    { key: 'count', header: 'Transactions', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.count.toLocaleString('en-IN')}</span> },
    { key: 'total', header: 'Total', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.total, currency)}</span> },
    { key: 'percentage', header: 'Share', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.percentage.toFixed(1)}%</span> },
  ];
}

export function PaymentBreakdownPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);

  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'payments',
    filter,
    queryFn: (f) => reportsApi.getPaymentBreakdown(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.modes ?? []).map((m) => ({ ...m, id: m.mode })),
    [data],
  );

  const pieData = useMemo(
    () => rows.map((r) => ({ label: r.mode, value: r.total / subunit, color: MODE_COLORS[r.mode] })),
    [rows, subunit],
  );

  const topMode = rows.length > 0 ? rows.reduce((a, b) => (a.total > b.total ? a : b)) : null;

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `payments-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Mode', get: (r: Row) => r.mode },
          { header: 'Count', get: (r: Row) => r.count },
          { header: `Total (${symbol})`, get: (r: Row) => (r.total / subunit).toFixed(csvDecimals) },
          { header: 'Share %', get: (r: Row) => r.percentage.toFixed(1) },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Payment Breakdown', `Total ${formatPaise(data?.total ?? 0, currency)} • Top: ${topMode?.mode ?? '—'}`);
    }
  };

  return (
    <ReportPageShell
      title="Payment Breakdown"
      subtitle="Sales split by tender type — Cash, UPI, Card, Wallet and Split payments."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Payments' }]}
      accent="brand-indigo"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'total', title: 'Total Collected', value: formatPaise(data?.total ?? 0, currency), tone: 'brand', icon: KpiIcons.rupee },
              { id: 'top', title: 'Top Mode', value: topMode?.mode ?? '—', subLabel: topMode ? `${topMode.percentage.toFixed(1)}% share` : undefined, tone: 'indigo', icon: KpiIcons.wallet },
              { id: 'modes', title: 'Modes Used', value: String(rows.length), tone: 'violet', icon: KpiIcons.tag },
              { id: 'txn', title: 'Total Txns', value: rows.reduce((s, r) => s + r.count, 0).toLocaleString('en-IN'), tone: 'teal', icon: KpiIcons.receipt },
            ]}
          />
          {pieData.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <PieChart data={pieData} title="Payment Mode Distribution" donut />
            </div>
          ) : null}
          <ReportTable title="Payment Modes" columns={columns} data={rows} emptyTitle="No payment data for selected period" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
